import { z } from "zod";

/**
 * A point-in-time answer to "where is the server at?" — how far each
 * subsystem's cursor has got, and which instance is answering (debug API,
 * docs/client-telemetry.md).
 *
 * Every timestamp is nullable because every one of them can legitimately be
 * "never": a fresh database has synced nothing, a load has detected nothing,
 * a hive has never been heard from. `null` is the answer, not missing data.
 *
 * Ages are computed server-side alongside their timestamps so a caller reads
 * lag rather than its own clock skew.
 */
export const serverStatusSchema = z
  .object({
    /** The server's clock when this was gathered — the anchor for every age. */
    now: z.coerce.date(),
    instance: z.object({
      /** Container hostname. Two different values across polls = two instances live. */
      serverName: z.string().nullable(),
      release: z.string().nullable(),
      startedAt: z.coerce.date(),
      uptimeSeconds: z.number().int(),
    }),
    /** The WHS pull (syncSensorData) — its cursor and how fresh its newest row is. */
    sync: z.object({
      maxWifihivescaleId: z.number().int().nullable(),
      newestSyncedReadingAt: z.coerce.date().nullable(),
      newestSyncedReadingAgeSeconds: z.number().int().nullable(),
    }),
    /** Newest data from *any* source, to tell a stalled sync from a quiet fleet. */
    ingest: z.object({
      newestReadingAt: z.coerce.date().nullable(),
      newestSubmissionAt: z.coerce.date().nullable(),
    }),
    /** The write-ahead log's drain: a rising `pending` means it is losing. */
    submissions: z.object({
      pending: z.number().int(),
      success: z.number().int(),
      failed: z.number().int(),
      invalid: z.number().int(),
      oldestPendingAt: z.coerce.date().nullable(),
      oldestPendingAgeSeconds: z.number().int().nullable(),
    }),
    /** Load detection cursors (ADR-0002 §6.3) — the laggard is what matters. */
    detection: z.object({
      neverProcessed: z.number().int(),
      oldestCursorAt: z.coerce.date().nullable(),
      newestCursorAt: z.coerce.date().nullable(),
      oldestCursorAgeSeconds: z.number().int().nullable(),
    }),
    /** Hive connectivity cursors (ADR-0003), same shape. */
    connectivity: z.object({
      neverHeard: z.number().int(),
      oldestHeardAt: z.coerce.date().nullable(),
      newestHeardAt: z.coerce.date().nullable(),
      oldestHeardAgeSeconds: z.number().int().nullable(),
    }),
  })
  .meta({ id: "ServerStatus" });

export type ServerStatus = z.infer<typeof serverStatusSchema>;
